import { useEffect, useState } from "react";
import { LinkedTasksPanel } from "@/components/shared/LinkedTasksPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, SelectField, DateField } from "@/components/form/Fields";
import { MultiSelect } from "@/components/form/MultiSelect";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { checkDuplicates, DuplicateDialog } from "@/components/shared/DuplicateCheck";
import { ModalNotes } from "@/components/shared/ModalNotes";
import { FileDown } from "lucide-react";
import { loadTermsTemplates, pickTerms } from "@/components/vouchers/TermsTemplates";
import { VoucherWinners } from "@/components/vouchers/VoucherWinners";
import { GiveawaySource, GiveawayLink } from "@/components/vouchers/GiveawayLinks";

export function VoucherPdfPreview({ id, onClose }) {
  const [pdf, setPdf] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let url = null, img = null;
    setPdf(null);
    client.get(`/vouchers/${id}/pdf`, { responseType: "blob" }).then(async (res) => {
      const pre = await client.get(`/vouchers/${id}/pdf`, { params: { preview: true }, responseType: "blob" });
      url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      img = URL.createObjectURL(pre.data);
      setPdf({ url, img, name: /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "Gift-Voucher.pdf" });
    }).catch(() => { toast.error("Could not create the voucher PDF"); onClose(); });
    return () => { if (url) URL.revokeObjectURL(url); if (img) URL.revokeObjectURL(img); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const download = () => Object.assign(document.createElement("a"), { href: pdf.url, download: pdf.name }).click();
  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl" data-testid="voucher-pdf-preview">
        <DialogHeader><DialogTitle className="font-heading">Gift Voucher Preview</DialogTitle></DialogHeader>
        <div className="flex aspect-[210/99] w-full items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {pdf ? <img src={pdf.img} alt="Gift voucher preview" className="h-full w-full object-contain" data-testid="voucher-pdf-frame" />
            : <div className="text-sm text-slate-500">Creating voucher…</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="voucher-pdf-close">Close</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" onClick={download} disabled={!pdf} data-testid="voucher-pdf-download"><FileDown className="mr-1.5 h-4 w-4" />Download PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function voucherStatus(v) {
  if (v.cancelled || v.cancelled_date) return "Cancelled";
  if (v.redeemed || v.redeemed_date) return "Redeemed";
  const today = new Date().toLocaleDateString("en-CA");
  if (v.date_expiry && v.date_expiry.slice(0, 10) < today) return "Expired";
  return "Active";
}

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Redeemed: "bg-sky-50 text-sky-700 border-sky-200",
  Expired: "bg-rose-50 text-rose-700 border-rose-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export function VoucherStatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-xs font-medium", cls)}>{status}</span>;
}

function CheckTick({ label, checked, onChange, testid }) {
  return (
    <label className={cn("flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors", checked ? "border-sky-300 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-700 hover:bg-slate-50")}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
      {label}
    </label>
  );
}

export function VoucherModal({ open, record, prefill = {}, onClose, onSuccess }) {
  const { users, user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({});
  const [redeemed, setRedeemed] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [reminderOn, setReminderOn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const [localRecord, setLocalRecord] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [autoTerms, setAutoTerms] = useState("");

  useEffect(() => {
    if (!open) return;
    const init = record || { issued_by_id: user?.id, issued_by_type: user?.id ? "user" : null, date_issued: new Date().toLocaleDateString("en-CA"), winners: [], ...prefill };
    setForm(init);
    setRedeemed(Boolean(init.redeemed || init.redeemed_date));
    setCancelled(Boolean(init.cancelled || init.cancelled_date));
    setReminderOn(Boolean(init.reminder_user_id || init.reminder_date || init.reminder_message));
    setLocalRecord(null);
    setDup(null);
    client.get("/organisations", { params: { lite: true } }).then(({ data }) => setOrgs(data)).catch(() => {});
    client.get("/contacts").then(({ data }) => setContacts(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
    loadTermsTemplates().then((t) => {
      setTemplates(t);
      if (record || init.terms) return;
      const txt = pickTerms(t, init.property_id)?.text || "";
      setAutoTerms(txt);
      setForm((f) => (f.terms ? f : { ...f, terms: txt }));
    }).catch(() => {});
  }, [open, record]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const effRecord = record || localRecord;
  const isEdit = Boolean(effRecord?.id);
  const orgOpts = orgs.filter((o) => o.org_type === "DTR Property").map((o) => ({ value: o.id, label: o.name }));
  if (form.property_id && !orgs.some((o) => o.id === form.property_id)) {
    orgOpts.unshift({ value: form.property_id, label: form.property_name || prefill.property_name || "Selected property" });
  }
  const code = orgs.find((o) => o.id === form.property_id)?.property_code || "";
  const prefixRx = /^[A-Z]{3}-/;
  const vnoPart = (form.voucher_no || "").replace(code ? new RegExp(`^${code}-`, "i") : prefixRx, "");
  const setVno = (v) => setForm((f) => ({ ...f, voucher_no: v ? (code ? `${code}-${v}` : v) : "" }));
  const userOpts = users.map((u) => ({ value: u.id, label: u.name }));
  const contactName = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "-";
  const issuedByOpts = users.map((u) => ({ value: `user:${u.id}`, label: u.name }));
  // Keep a legacy contact selection visible on older vouchers
  if (form.issued_by_id && form.issued_by_type === "contact") {
    const c = contacts.find((x) => x.id === form.issued_by_id);
    issuedByOpts.push({ value: `contact:${form.issued_by_id}`, label: c ? `${contactName(c)} · Contact` : form.issued_by_name || "Contact" });
  }
  const issuedByVal = form.issued_by_id ? `${form.issued_by_type || "user"}:${form.issued_by_id}` : null;
  const setIssuedBy = (arr) => {
    const v = arr[arr.length - 1] || null;
    if (!v) return setForm((f) => ({ ...f, issued_by_id: null, issued_by_type: null }));
    const [t, id] = v.split(":");
    setForm((f) => ({ ...f, issued_by_id: id, issued_by_type: t }));
  };

  const toggleRedeemed = (val) => { setRedeemed(val); if (val) setCancelled(false); };

  const [preGiveaway, setPreGiveaway] = useState(null);
  const applyGiveaway = (g) => {
    if (!g) {
      if (preGiveaway) setForm((f) => ({ ...f, ...preGiveaway, collab_id: null }));
      setPreGiveaway(null);
      return;
    }
    if (!preGiveaway) setPreGiveaway({ issued_to: form.issued_to, property_id: form.property_id, voucher_no: form.voucher_no, winners: form.winners, inclusions: form.inclusions, terms: form.terms });
    const pid = g.property_ids.find((id) => orgs.some((o) => o.id === id && o.org_type === "DTR Property")) || g.property_ids[0] || form.property_id;
    const nc = orgs.find((o) => o.id === pid)?.property_code;
    const nt = !form.terms || form.terms === autoTerms ? pickTerms(templates, pid)?.text || "" : null;
    if (nt !== null) setAutoTerms(nt);
    setForm((f) => ({
      ...f, collab_id: g.id, issued_to: g.title || f.issued_to, property_id: pid || null,
      voucher_no: vnoPart ? (nc ? `${nc}-${vnoPart}` : vnoPart) : f.voucher_no,
      winners: g.winners.length ? g.winners.map((w, i) => ({ ...w, id: `g-${i}` })) : f.winners,
      inclusions: g.our_offer || f.inclusions, ...(nt !== null ? { terms: nt } : {}),
    }));
  };
  const toggleCancelled = (val) => { setCancelled(val); if (val) setRedeemed(false); };

  const buildPayload = () => {
    const p = { ...form, redeemed, cancelled };
    if (!redeemed) { p.redeemed_date = ""; p.redeemed_by_id = ""; p.redeemed_note = ""; p.stay_dates = ""; }
    if (!cancelled) { p.cancelled_date = ""; p.cancelled_by_id = ""; p.cancelled_reason = ""; }
    if (!reminderOn) { p.reminder_user_id = ""; p.reminder_date = ""; p.reminder_message = ""; }
    return p;
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) delete payload.notes;
      if (isEdit) await client.patch(`/vouchers/${effRecord.id}`, payload);
      else await client.post("/vouchers", payload);
      toast.success(isEdit ? "Voucher updated" : "Voucher created");
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!form.property_id) return toast.error("Please choose a Property");
    if (!isEdit) {
      setSaving(true);
      const matches = await checkDuplicates("voucher", {
        voucher_no: form.voucher_no, date_issued: form.date_issued, date_expiry: form.date_expiry, property_id: form.property_id,
      });
      setSaving(false);
      if (matches.length) { setDup(matches); return; }
    }
    await doSave();
  };

  const openExisting = async (m) => {
    setDup(null);
    try {
      const { data } = await client.get(`/vouchers/${m.id}`);
      setLocalRecord(data);
      setForm(data);
      setRedeemed(Boolean(data.redeemed || data.redeemed_date));
      setCancelled(Boolean(data.cancelled || data.cancelled_date));
    } catch { toast.error("Could not open voucher"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="voucher-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {isEdit ? "Edit Prize Voucher" : "New Prize Voucher"}
            {isEdit && <VoucherStatusPill status={voucherStatus({ ...form, redeemed, cancelled })} />}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!isEdit && <GiveawaySource onPick={applyGiveaway} />}
          {form.collab_id && isEdit && <GiveawayLink id={form.collab_id} title={form.collab_title} />}
          <MultiSelect
            label="Property"
            value={form.property_id ? [form.property_id] : []}
            onChange={(arr) => {
              const pid = arr[arr.length - 1] || null;
              const nc = orgs.find((o) => o.id === pid)?.property_code;
              const nt = !isEdit && (!form.terms || form.terms === autoTerms) ? pickTerms(templates, pid)?.text || "" : null;
              if (nt !== null) setAutoTerms(nt);
              setForm((f) => ({ ...f, property_id: pid, voucher_no: vnoPart ? (nc ? `${nc}-${vnoPart}` : vnoPart) : f.voucher_no, ...(nt !== null ? { terms: nt } : {}) }));
            }}
            options={orgOpts}
            testid="voucher-field-property"
            placeholder="Search to select…"
          />
          <div className="grid grid-cols-2 gap-3">
            <DateField label="Issued" value={form.date_issued} onChange={set("date_issued")} testid="voucher-field-issued" />
            <DateField label="Expiry" value={form.date_expiry} onChange={set("date_expiry")} testid="voucher-field-expiry" monthHint={form.date_issued} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Room" value={form.room} onChange={set("room")} testid="voucher-field-room" />
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Voucher No</label>
              <div className="flex h-10 overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100">
                {code && <span className="flex items-center border-r border-slate-200 bg-slate-50 px-2.5 font-mono text-sm font-semibold text-slate-500" title="Property code (set on the property)" data-testid="voucher-field-code">{code}-</span>}
                <input value={vnoPart} onChange={(e) => setVno(e.target.value)} className="min-w-0 flex-1 px-3 text-sm outline-none" data-testid="voucher-field-no" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Gift</label>
              <div className="flex h-10 overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100">
                <input type="number" min="1" max="60" value={form.nights || ""} onChange={(e) => set("nights")(e.target.value ? parseInt(e.target.value, 10) : null)} className="w-14 min-w-0 px-3 text-sm outline-none" placeholder="X" data-testid="voucher-field-nights" />
                <span className="flex items-center border-l border-slate-200 bg-slate-50 px-2.5 text-sm text-slate-500">Night Stay</span>
              </div>
              {!form.nights && form.gift && <p className="mt-1 text-[11px] text-amber-700" data-testid="voucher-gift-legacy">Was: “{form.gift}”</p>}
            </div>
            <TextField label="Issued To (giveaway)" value={form.issued_to} onChange={set("issued_to")} testid="voucher-field-issuedto" />
          </div>
          <MultiSelect
            label="Issued By"
            value={issuedByVal ? [issuedByVal] : []}
            onChange={setIssuedBy}
            options={issuedByOpts}
            testid="voucher-field-issuedby"
            placeholder="Search users…"
          />
          <VoucherWinners value={form.winners || []} onChange={set("winners")} />

          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="voucher-inclusions-section">
            <h4 className="mb-2 font-heading text-sm font-semibold text-amber-800">Inclusions</h4>
            <TextArea value={form.inclusions} onChange={set("inclusions")} testid="voucher-field-inclusions" rows={3} placeholder="List what's included with this prize (e.g. 2 nights accommodation, breakfast, late checkout)…" />
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 text-sm font-medium text-amber-800">
              <input type="checkbox" checked={reminderOn} onChange={(e) => setReminderOn(e.target.checked)} data-testid="voucher-toggle-reminder" className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
              Set a reminder
            </label>
            {reminderOn && (
              <div className="mt-3 space-y-3 border-t border-amber-200 pt-3" data-testid="voucher-reminder-section">
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="Notify User" value={form.reminder_user_id} onChange={set("reminder_user_id")} options={userOpts} testid="voucher-field-reminder-user" />
                  <DateField label="On Date" value={form.reminder_date} onChange={set("reminder_date")} testid="voucher-field-reminder-date" />
                </div>
                <TextArea label="Message" value={form.reminder_message} onChange={set("reminder_message")} testid="voucher-field-reminder-message" rows={2} placeholder="Message to show the user on that date…" />
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="voucher-terms-section">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h4 className="font-heading text-sm font-semibold text-slate-800">Terms & Conditions</h4>
              <select value="" onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) set("terms")(t.text); }}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-600 focus:border-sky-400 focus:outline-none" data-testid="voucher-terms-template">
                <option value="">Fill from template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <TextArea value={form.terms} onChange={set("terms")} rows={4} testid="voucher-field-terms" placeholder="Printed at the bottom of the voucher PDF…" />
          </div>

          <ModalNotes entityType="voucher" collection="vouchers" record={effRecord} value={form.notes} onChange={set("notes")} testid="voucher-field-notes" />
          {isEdit && <LinkedTasksPanel parentType="voucher" parentId={effRecord.id} parentLabel={form.voucher_no ? `#${form.voucher_no}` : "Voucher"} prefill={{ property_ids: form.property_id ? [form.property_id] : [] }} />}

          <div className="grid grid-cols-2 gap-3">
            <CheckTick label="Redeemed" checked={redeemed} onChange={toggleRedeemed} testid="voucher-toggle-redeemed" />
            <CheckTick label="Cancel Voucher" checked={cancelled} onChange={toggleCancelled} testid="voucher-toggle-cancelled" />
          </div>

          {redeemed && (
            <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid="voucher-redeemed-section">
              <h4 className="mb-3 font-heading text-sm font-semibold text-slate-900">Redeemed</h4>
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Redeemed On" value={form.redeemed_date} onChange={set("redeemed_date")} testid="voucher-field-redeemed-date" />
                <SelectField label="User" value={form.redeemed_by_id} onChange={set("redeemed_by_id")} options={userOpts} testid="voucher-field-redeemed-by" />
              </div>
              <div className="mt-3"><TextField label="Stay Dates" value={form.stay_dates} onChange={set("stay_dates")} testid="voucher-field-stay-dates" /></div>
              <div className="mt-3"><TextArea label="Note" value={form.redeemed_note} onChange={set("redeemed_note")} testid="voucher-field-redeemed-note" rows={2} /></div>
            </div>
          )}

          {cancelled && (
            <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-3" data-testid="voucher-cancelled-section">
              <h4 className="mb-3 font-heading text-sm font-semibold text-slate-900">Cancelled</h4>
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Date" value={form.cancelled_date} onChange={set("cancelled_date")} testid="voucher-field-cancelled-date" />
                <SelectField label="User" value={form.cancelled_by_id} onChange={set("cancelled_by_id")} options={userOpts} testid="voucher-field-cancelled-by" />
              </div>
              <div className="mt-3"><TextArea label="Reason" value={form.cancelled_reason} onChange={set("cancelled_reason")} testid="voucher-field-cancelled-reason" rows={2} /></div>
            </div>
          )}
        </div>
        <DialogFooter>
          {isEdit && <Button variant="outline" className="mr-auto" onClick={() => setPreviewId(effRecord.id)} data-testid="voucher-download-pdf"><FileDown className="mr-1.5 h-4 w-4" />Voucher PDF</Button>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" onClick={submit} disabled={saving} data-testid="voucher-save-button">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Voucher"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <DuplicateDialog
        open={Boolean(dup)}
        matches={dup || []}
        entityName="voucher"
        onCancel={() => setDup(null)}
        onContinue={() => { setDup(null); doSave(); }}
        onOpenExisting={openExisting}
      />
      <VoucherPdfPreview id={previewId} onClose={() => setPreviewId(null)} />
    </Dialog>
  );
}
